(() => {
  const KEY = 'PeakStyleProductCardAvailabilityOverlay';
  const VERSION = '2.1.1';
  const previous = window[KEY];

  if (previous?.version === VERSION) return;
  previous?.destroy?.();

  const CARD_SELECTOR = '.product-card';
  const SWATCH_SELECTOR = '.color-swatch__item[data-value][data-option-position]';
  const DATA_SELECTOR = 'script.product-card-availability-data';
  const parsedCards = new WeakMap();
  const listingContext =
    document.body.classList.contains('template-collection') ||
    document.body.classList.contains('template-search') ||
    window.location.pathname.includes('/collections/') ||
    window.location.pathname === '/search';

  function getRawVariantScript(card) {
    const existing = card.querySelector(DATA_SELECTOR);
    if (existing) return existing;

    const options = card.querySelector('.product-card__options');
    if (!options) return null;

    const scripts = [...options.querySelectorAll('script[type="application/json"]')];
    const script = scripts.find(node => {
      try {
        const value = JSON.parse(node.textContent || '[]');
        return Array.isArray(value) && value.some(item => item && ('option1' in item || 'options' in item));
      } catch (_) {
        return false;
      }
    });

    if (script) script.classList.add('product-card-availability-data');
    return script || null;
  }

  function optionValue(variant, position) {
    return variant?.[`option${position}`] ?? variant?.options?.[position - 1] ?? '';
  }

  function inferSizePosition(variants, colorPosition) {
    const candidates = [1, 2, 3].filter(position => position !== colorPosition);

    const scored = candidates.map(position => {
      const values = new Set(
        variants
          .map(variant => String(optionValue(variant, position) || '').trim())
          .filter(value => value && value.toLowerCase() !== 'default title')
      );
      return { position, score: values.size };
    });

    scored.sort((a, b) => b.score - a.score || a.position - b.position);
    return scored[0]?.score ? scored[0].position : null;
  }

  function parseCard(card) {
    if (parsedCards.has(card)) return parsedCards.get(card);

    const swatch = card.querySelector(SWATCH_SELECTOR);
    const script = getRawVariantScript(card);
    const colorPosition = Number(swatch?.dataset.optionPosition);

    if (!script || !Number.isInteger(colorPosition) || colorPosition < 1) {
      parsedCards.set(card, null);
      return null;
    }

    try {
      const variants = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(variants)) throw new Error('Invalid variant data');

      const sizePosition = inferSizePosition(variants, colorPosition);
      if (!sizePosition) throw new Error('No size option found');

      const rows = variants.map(variant => ({
        color: String(optionValue(variant, colorPosition) || '').trim(),
        size: String(optionValue(variant, sizePosition) || '').trim(),
        available: variant.available === true
      }));

      script.dataset.colorPosition = String(colorPosition);
      script.dataset.sizePosition = String(sizePosition);

      const data = { rows, colorPosition, sizePosition };
      parsedCards.set(card, data);
      return data;
    } catch (_) {
      parsedCards.set(card, null);
      return null;
    }
  }

  function removeUnavailableListingCard(card) {
    if (!listingContext || !(card instanceof Element)) return;
    const column = card.closest('.f-column');
    if (column?.parentElement?.matches('[data-products-grid]')) {
      column.remove();
    } else {
      card.remove();
    }
  }

  async function verifyProductAvailability(card) {
    if (!listingContext || !(card instanceof Element)) return true;
    if (card.dataset.peakstyleProductAvailable === 'true') return true;
    if (card.dataset.peakstyleProductAvailable === 'false') return false;

    const link = card.querySelector('.product-card__link[href], .full-width-link[href]');
    if (!link) return true;

    try {
      const url = new URL(link.href, window.location.origin);
      const response = await fetch(`${url.pathname}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      const available = Array.isArray(product.variants)
        ? product.variants.some(variant => variant.available === true)
        : product.available === true;
      card.dataset.peakstyleProductAvailable = available ? 'true' : 'false';
      return available;
    } catch (error) {
      console.warn('PeakStyle product availability check failed', error);
      card.dataset.peakstyleProductAvailable = 'true';
      return true;
    }
  }

  function enforceListingAvailability(card, data) {
    if (!listingContext) return true;

    if (data?.rows?.length) {
      const available = data.rows.some(row => row.available === true);
      card.dataset.peakstyleProductAvailable = available ? 'true' : 'false';
      if (!available) removeUnavailableListingCard(card);
      return available;
    }

    if (card.dataset.peakstyleAvailabilityChecking === 'true') return false;
    if (card.dataset.peakstyleProductAvailable === 'true') return true;
    if (card.dataset.peakstyleProductAvailable === 'false') {
      removeUnavailableListingCard(card);
      return false;
    }

    card.dataset.peakstyleAvailabilityChecking = 'true';
    verifyProductAvailability(card).then(available => {
      delete card.dataset.peakstyleAvailabilityChecking;
      if (!card.isConnected) return;
      if (!available) {
        removeUnavailableListingCard(card);
        return;
      }
      prepareCard(card);
    });
    return false;
  }

  function availableSizes(card, color) {
    const data = parseCard(card);
    if (!data) return [];

    const seen = new Set();
    const sizes = [];

    for (const row of data.rows) {
      if (!row.available || row.color !== color || !row.size || seen.has(row.size)) continue;
      seen.add(row.size);
      sizes.push(row.size);
    }

    return sizes;
  }

  function ensureDesktopOverlay(card) {
    const media = card.querySelector('.product-card__image-wrapper');
    if (!media) return null;

    let overlay = media.querySelector(':scope > .peakstyle-card-availability-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'peakstyle-card-availability-overlay';
      overlay.setAttribute('aria-live', 'polite');
      overlay.innerHTML = '<div class="peakstyle-card-availability-overlay__label">Verfügbare Größen</div><div class="peakstyle-card-availability-overlay__sizes"></div>';
      media.appendChild(overlay);
    }

    return overlay;
  }

  function ensureMobileLine(card) {
    let line = card.querySelector(':scope > .peakstyle-card-availability-mobile');
    if (line) return line;

    line = document.createElement('div');
    line.className = 'peakstyle-card-availability-mobile';
    line.setAttribute('aria-live', 'polite');

    const options = [...card.children].find(child => child.classList?.contains('product-card__options'));
    (options || card.querySelector('.product-card__wrapper'))?.insertAdjacentElement('afterend', line);
    return line;
  }

  function render(card, color, { activate = false, updateMobile = true } = {}) {
    const overlay = ensureDesktopOverlay(card);
    if (!overlay) return;

    const sizes = availableSizes(card, color);
    const label = overlay.querySelector('.peakstyle-card-availability-overlay__label');
    const container = overlay.querySelector('.peakstyle-card-availability-overlay__sizes');

    label.textContent = color ? `Verfügbare Größen · ${color}` : 'Verfügbare Größen';
    container.replaceChildren();

    if (sizes.length) {
      for (const size of sizes) {
        const item = document.createElement('span');
        item.className = 'peakstyle-card-availability-overlay__size';
        item.textContent = size;
        container.appendChild(item);
      }
    } else {
      const empty = document.createElement('span');
      empty.className = 'peakstyle-card-availability-overlay__empty';
      empty.textContent = 'Aktuell ausverkauft';
      container.appendChild(empty);
    }

    overlay.hidden = false;
    card.classList.toggle('peakstyle-availability-active', activate);

    if (updateMobile) {
      const mobile = ensureMobileLine(card);
      if (mobile) {
        mobile.hidden = false;
        mobile.textContent = sizes.length
          ? `Größen ${color}: ${sizes.join(' · ')}`
          : `${color}: aktuell ausverkauft`;
      }
    }
  }

  function deactivate(card) {
    card?.classList.remove('peakstyle-availability-active');

    const selected = card?.dataset.peakstyleSelectedColor;
    if (selected) render(card, selected, { activate: false, updateMobile: true });
  }

  function prepareCard(card) {
    if (!(card instanceof Element) || card.dataset.peakstyleAvailabilityPrepared === 'true') return;

    const data = parseCard(card);
    if (!enforceListingAvailability(card, data)) return;
    if (!data) return;

    card.dataset.peakstyleAvailabilityPrepared = 'true';
    const firstSwatch = card.querySelector(SWATCH_SELECTOR);
    const initialColor = firstSwatch?.dataset.value || data.rows.find(row => row.available)?.color || data.rows[0]?.color;

    if (initialColor) {
      card.dataset.peakstyleSelectedColor = initialColor;
      render(card, initialColor, { activate: false, updateMobile: true });
    }
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches(CARD_SELECTOR)) prepareCard(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(prepareCard);
  }

  function swatchFromEvent(event) {
    return event.target.closest?.(SWATCH_SELECTOR);
  }

  function onPointerOver(event) {
    const swatch = swatchFromEvent(event);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    prepareCard(card);
    render(card, swatch.dataset.value || '', { activate: true, updateMobile: true });
  }

  function onPointerOut(event) {
    const swatch = swatchFromEvent(event);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    const nextSwatch = event.relatedTarget?.closest?.(SWATCH_SELECTOR);
    if (nextSwatch?.closest(CARD_SELECTOR) === card) return;

    deactivate(card);
  }

  function onFocusIn(event) {
    const swatch = swatchFromEvent(event);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    prepareCard(card);
    render(card, swatch.dataset.value || '', { activate: true, updateMobile: true });
  }

  function onFocusOut(event) {
    const swatch = swatchFromEvent(event);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    const nextSwatch = event.relatedTarget?.closest?.(SWATCH_SELECTOR);
    if (nextSwatch?.closest(CARD_SELECTOR) === card) return;

    deactivate(card);
  }

  function onClick(event) {
    const swatch = swatchFromEvent(event);
    if (!swatch) return;

    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    const color = swatch.dataset.value || '';
    card.dataset.peakstyleSelectedColor = color;
    render(card, color, { activate: true, updateMobile: true });
  }

  const mutationObserver = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) scan(node);
      }
    }
  });

  function init() {
    scan();
    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointerout', onPointerOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('shopify:section:load', event => scan(event.target));
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  function destroy() {
    mutationObserver.disconnect();
    document.removeEventListener('pointerover', onPointerOver, true);
    document.removeEventListener('pointerout', onPointerOut, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('click', onClick, true);
    document.querySelectorAll('.peakstyle-availability-active').forEach(card => {
      card.classList.remove('peakstyle-availability-active');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window[KEY] = { version: VERSION, refresh: () => scan(), destroy };
})();
